import { BlindMapQuestion } from './BlindMapQuestion';
import { Section } from './contentBuilder/Section';
import { Hotspot } from './Hotspot';
import { HotspotTab } from './HotspotTab';
import { Item } from './Item';
import { ItemType } from './ItemType';
import { Question } from './Question';
import { Resource } from './Resource';
import { SequencedFlowStep } from './SequencedFlowStep';
import { StartingMessage } from './StartingMessage';
import { Guid } from '../types/identifiers/guid';
import { isStringEmptyOrWhiteSpace } from '../utils/isStringEmptyOrWhiteSpace';

/**
 * The item types that can be created through the `EditAction_CreateNewContentItem` action - i.e.
 * everything the manager's `CreateNewContentItem` thunk builds. Items that live in the menu tree
 * (`MenuItem`, `Quiz`, `SequencedFlowItem`, `NonSequencedFlowItem`, `ResourcesFolder`) go through
 * `EditAction_CreateNewItem` instead and are not part of this union.
 */
export type NewContentItem =
  | BlindMapQuestion
  | Hotspot
  | HotspotTab
  | Question
  | Resource
  | Section
  | SequencedFlowStep
  | StartingMessage;

/** The `ItemType`s of {@link NewContentItem}, in the same order. */
export const NEW_CONTENT_ITEM_TYPES: readonly ItemType[] = [
  ItemType.BlindMapQuestion,
  ItemType.Hotspot,
  ItemType.HotspotTab,
  ItemType.Question,
  ItemType.Resource,
  ItemType.Section,
  ItemType.SequencedFlowStep,
  ItemType.StartingMessage,
];

export const isNewContentItem = (item: Item): item is NewContentItem => NEW_CONTENT_ITEM_TYPES.includes(item.type);

export function ensureNewContentItem(item: Item): asserts item is NewContentItem {
  if (!isNewContentItem(item)) {
    throw Error(`Expected a creatable content item, got '${item.type}'. Use EditAction_CreateNewItem for menu items.`);
  }
}

/**
 * Payload of the `EditAction_CreateNewContentItem` redux action, dispatched by the manager's
 * `CreateNewContentItem` thunk and consumed by the items/edit/side menu reducers, the api
 * middleware (`newContentItemApi`) and `managerMiscMiddleware`.
 *
 * Build it with {@link createNewContentItemPayload} rather than by hand.
 */
export type CreateNewContentItemPayload = {
  /** The already built content item - reducers insert it under `parentItemId` as is. */
  readonly item: NewContentItem;
  /** Id of the item the new content item is added under. Reducers bail out when it is unknown. */
  readonly parentItemId: Guid;
  /** Open the new content item for editing once created. Treated as `false` when absent. */
  readonly select?: boolean;
  /** Set when the item was produced by a copy/paste or duplicate - drives the "Copy of ..." flash message. */
  readonly isCopy?: boolean;
  /** Title of the item that was copied. Required when `isCopy` is set, meaningless otherwise. */
  readonly originalTitle?: string;
};

export type CreateNewContentItemPayloadOptions = {
  /** Defaults to the parent the item was built with (`item.parentId.parentId`). */
  parentItemId?: Guid;
  select?: boolean;
  isCopy?: boolean;
  originalTitle?: string;
};

/**
 * Builds the payload for the `EditAction_CreateNewContentItem` action.
 *
 * The item type is checked both at compile time ({@link NewContentItem}) and at runtime, and the
 * parent is resolved from the item itself when not given, so a payload that reaches a reducer
 * always has a creatable item and a parent to put it under.
 */
export const createNewContentItemPayload = (
  item: NewContentItem,
  { parentItemId, select, isCopy, originalTitle }: CreateNewContentItemPayloadOptions = {}
): CreateNewContentItemPayload => {
  ensureNewContentItem(item);

  const parent = parentItemId ?? item.parentId.parentId;
  if (!parent) {
    throw Error(`Content item '${item.id}' (${item.type}) has no parent - a content item always belongs to an item.`);
  }

  if (isCopy && originalTitle === undefined) {
    throw Error('originalTitle is required when isCopy is set.');
  }

  return { item, parentItemId: parent, select, isCopy, originalTitle };
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isNewContentItemLike = (value: unknown): value is NewContentItem =>
  isRecord(value) &&
  typeof value.id === 'string' &&
  isRecord(value.parentId) &&
  isNewContentItem(value as unknown as Item);

/**
 * Narrows an untyped action payload to {@link CreateNewContentItemPayload}.
 *
 * `item` is checked structurally (id, parentId, creatable {@link ItemType}) rather than field by
 * field per item type - enough to reject another action's payload, not a full validator.
 */
export const isCreateNewContentItemPayload = (payload: unknown): payload is CreateNewContentItemPayload =>
  isRecord(payload) &&
  isNewContentItemLike(payload.item) &&
  typeof payload.parentItemId === 'string' &&
  !isStringEmptyOrWhiteSpace(payload.parentItemId) &&
  (payload.select === undefined || typeof payload.select === 'boolean') &&
  (payload.isCopy === undefined || typeof payload.isCopy === 'boolean') &&
  (payload.originalTitle === undefined || typeof payload.originalTitle === 'string');

export function ensureCreateNewContentItemPayload(payload: unknown): asserts payload is CreateNewContentItemPayload {
  if (!isCreateNewContentItemPayload(payload)) {
    throw Error('Expected payload to be a CreateNewContentItemPayload.');
  }
}
